import type {
  TranscriptionStateCall,
  TranscriptionStateKeywordMatchedToken,
  TranscriptionStateToken,
} from '../transcriptionSlice/transcriptionSlice.types';

/**
 * Merge tokens with the same speaker name in a transcription
 * without mutating the original transcription object to respect
 * immutability.
 * @param {object} transcription
 * @returns transcription with merged tokens
 */
const mergeTokens = (
  transcription: TranscriptionStateCall,
): TranscriptionStateCallWithMergedTokens | null => {
  if (!transcription || !transcription.payload) {
    return null;
  }

  const { tokens = [] } = transcription.payload;

  const mergedTokens = tokens.reduce<
    (TranscriptionStateToken | CompositeTranscriptionStateToken)[]
  >((acc, token) => {
    const lastToken = acc[acc.length - 1];
    if (lastToken && lastToken.speaker_id === token.speaker_id) {
      const lastTranscript = `${lastToken.transcript}`.trim();
      const currentTranscript = `${token.transcript}`.trim();

      let mergedTranscript = `${lastTranscript}`;
      if (currentTranscript) {
        mergedTranscript = `${lastTranscript} ${currentTranscript}`;
      }

      // Merge the translations as well.
      const lastTranslatedTranscript = lastToken?.translated_transcript;
      const currentTranslatedTranscript = token?.translated_transcript;

      let mergedTranslatedTranscript = null;

      // If there's a translation in either the last token or the current token,
      // then merge them together since they belong to the same speaker.
      if (lastTranslatedTranscript || currentTranslatedTranscript) {
        mergedTranslatedTranscript = {
          ...(lastTranslatedTranscript || {}),
          ...(currentTranslatedTranscript || {}),
        };

        let lastTranslation = `${lastTranslatedTranscript?.en || ''}`.trim();

        // edge-case: when there's no last translation, use the original as a
        // placeholder to keep the overall conversation in sync between the
        // original and translated versions.
        if (!lastTranslation) {
          lastTranslation = `${lastTranscript}`.trim();
        }

        const currentTranslation = `${
          currentTranslatedTranscript?.en || ''
        }`.trim();

        let mergedTranslation = `${lastTranslation}`;
        if (currentTranslation) {
          mergedTranslation = `${lastTranslation} ${currentTranslation}`.trim();
        }

        if (!currentTranslation && lastTranslatedTranscript) {
          // edge-case: when there's no current translation, use the original
          // as a placeholder to keep the overall conversation in sync between
          // the original and translated versions.
          mergedTranslation = `${lastTranslation} ${currentTranscript}`.trim();
        }
        mergedTranslatedTranscript.en = mergedTranslation;
      }

      // build the set of tokens that were merged into this one it will consist of the lastToken (or if it is a composite itself, the tokens merged into it), and then this new token:
      const compositeTokens = [
        ...(isCompositeToken(lastToken) ? lastToken.merged : [lastToken]),
        token,
      ];

      const finalTranscription: CompositeTranscriptionStateToken = {
        ...lastToken,
        merged: compositeTokens,
        transcript: mergedTranscript,
      };

      if (mergedTranslatedTranscript) {
        finalTranscription.translated_transcript = mergedTranslatedTranscript;
        finalTranscription.translated = mergedTranslatedTranscript.en;
      }

      // If there's a translation present in the merged transcript but no
      // detected language, then set the detected language from the current
      // token since the last token was likely in english.
      if (
        finalTranscription.translated_transcript &&
        !finalTranscription.detected_language
      ) {
        finalTranscription.detected_language = token.detected_language;
      }

      // Replace the last token with the merged token
      return [...acc.slice(0, -1), finalTranscription];
    } else {
      // It's a new speaker, so just add the token to the list
      return [...acc, { ...token }];
    }
  }, []);

  return {
    ...transcription,
    payload: {
      ...transcription.payload,
      tokens: mergedTokens,
    },
  };
};

/**
 * Defines a token was merged with other tokens.
 */
export interface CompositeTranscriptionStateToken
  extends TranscriptionStateToken {
  /**
   * Provides the set of tokens that were merged into into this token.
   * It includes the original token and the subsequent tokens that were merged into this one in the same order that they were merged.
   */
  merged: TranscriptionStateToken[];
}

export interface TranscriptKeywordLocation {
  match_index: number;
  match_length: number;
}

/**
 * Returns true if the token is the specified token id, or is a @see CompositeTranscriptionStateToken that contains the specified token id.
 */
export function tokenContainsTokenId(
  token: TranscriptionStateToken | CompositeTranscriptionStateToken,
  token_id: string,
): boolean {
  if (!isCompositeToken(token)) {
    return token.token_id === token_id;
  }
  return token.merged.some(token => token.token_id === token_id);
}

/**
 * Returns the index and length of the specified keyword match within the specified token's transcript.
 * NOTE: This is used to specifically handle the possibility of the token being a @see CompositeTranscriptionStateToken.
 * @param withinToken The token to find the keyword within.
 * @param keywordMatch The matched keyword to find the location of.
 * @returns The index and length of the match or null if the match was not found.
 */
export function getKeywordLocation(
  withinToken: TranscriptionStateToken | CompositeTranscriptionStateToken,
  keywordMatch: TranscriptionStateKeywordMatchedToken,
): TranscriptKeywordLocation | null {
  if (!isCompositeToken(withinToken)) {
    return {
      match_index: keywordMatch.match_index,
      match_length: keywordMatch.match_length,
    };
  }

  // find the token that this keyword is within:
  const mergedTokenIndex = withinToken.merged.findIndex(
    token => token.token_id === keywordMatch.token_id,
  );

  if (mergedTokenIndex === -1) {
    // this token is not in the merged tokens, so return null:
    return null;
  }

  // add up all the transcripts + ' ' spaces to get the index of the match
  const priorTokens = withinToken.merged.slice(0, mergedTokenIndex);
  const transcriptLengthOfPriorTokens = priorTokens.reduce(
    (acc, token) => acc + token.transcript.length,
    0,
  );
  // if the token had an empty string, we don't count the space
  const spaceCount = priorTokens.filter(t => t.transcript).length;
  const matchIndex =
    transcriptLengthOfPriorTokens + spaceCount + keywordMatch.match_index;

  return {
    match_index: matchIndex,
    match_length: keywordMatch.match_length,
  };
}

/**
 * Defines a @see TranscriptionStateCall that can contain tokens that were merged.
 */
export interface TranscriptionStateCallWithMergedTokens
  extends TranscriptionStateCall {
  payload: {
    tokens: (CompositeTranscriptionStateToken | TranscriptionStateToken)[];
    speakers: TranscriptionStateCall['payload']['speakers'];
    languagesDetected: TranscriptionStateCall['payload']['languagesDetected'];
    audio_data: TranscriptionStateCall['payload']['audio_data'];
    call_information: TranscriptionStateCall['payload']['call_information'];
  };
}

/** Indicates if the specified token is a @see CompositeTranscriptionStateToken */
export function isCompositeToken(
  token: TranscriptionStateToken | CompositeTranscriptionStateToken,
): token is CompositeTranscriptionStateToken {
  return 'merged' in token;
}

export default mergeTokens;
